import { Button, Group, Menu, Modal, TextInput, Tooltip } from "@mantine/core";
import { IconChevronDown, IconPlus } from "@tabler/icons-react";
import { FC, useEffect, useState } from "react";
import {
  usePatchProjectByProjectIdTableTemplateMutation,
  usePostProjectByProjectIdTableTemplateMutation,
} from "../../../../../../openapi";
import { selectCurrentProjectId } from "../../../../../global/globalSliceCommon";
import { useAppDispatch, useAppSelector } from "../../../../../hooks";
import { selectActiveTableTemplate } from "../../issueSelectors";
import { convertIssueDropdownFiltersToTableTemplateFilter } from "../../issueUtils";
import { selectIssueBoardId } from "../../redux/selectors/issueCommonSelectors";

interface TemplateMenuProps {
  onSaveChanges: () => void;
  onSaveNew: () => void;
  hasActiveTemplate: boolean;
}

const TemplateMenu: FC<TemplateMenuProps> = ({ onSaveChanges, onSaveNew, hasActiveTemplate }) => (
  <Menu width={180} shadow="md" position="bottom-start" withArrow={false} closeOnItemClick withinPortal>
    <Tooltip label="Lagre endringer i mal" openDelay={200} withArrow offset={2} withinPortal>
      <Menu.Target>
        <Button
          id="saveTemplate"
          variant="secondary-filled"
          pr="xs"
          pl={6}
          size="sm"
          style={{ borderRadius: "0 6px 6px 0" }}
          rightSection={<IconChevronDown size={14} />}
        >
          <i className="fa fa-floppy-o fa-fw" />
        </Button>
      </Menu.Target>
    </Tooltip>
    <Menu.Dropdown>
      <Menu.Item onClick={onSaveNew} leftSection={<IconPlus size={14} />}>
        Lagre ny
      </Menu.Item>
      {hasActiveTemplate && (
        <Menu.Item onClick={onSaveChanges} leftSection={<i className="fa fa-pencil" />}>
          Lagre endringer
        </Menu.Item>
      )}
    </Menu.Dropdown>
  </Menu>
);

interface SaveChangesModalProps {
  isOpen: boolean;
  onClose: () => void;
  templateName: string;
  onTemplateNameChange: (value: string) => void;
  onSave: () => void;
}

const SaveChangesModal: FC<SaveChangesModalProps> = ({
  isOpen,
  onClose,
  templateName,
  onTemplateNameChange,
  onSave,
}) => (
  <Modal opened={isOpen} onClose={onClose} title="Rediger mal" centered>
    <TextInput
      label="Malnavn"
      value={templateName}
      onChange={(event) => onTemplateNameChange(event.currentTarget.value)}
      placeholder="Skriv inn nytt navn"
      required
    />
    <Group justify="flex-end" mt="md">
      <Button variant="outline" onClick={onClose}>
        Avbryt
      </Button>
      <Button color="primary" onClick={onSave} disabled={!templateName}>
        Lagre
      </Button>
    </Group>
  </Modal>
);

interface SaveNewTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  newTemplateName: string;
  onNewTemplateNameChange: (value: string) => void;
  onSaveNew: () => void;
}

const SaveNewTemplateModal: FC<SaveNewTemplateModalProps> = ({
  isOpen,
  onClose,
  newTemplateName,
  onNewTemplateNameChange,
  onSaveNew,
}) => (
  <Modal opened={isOpen} onClose={onClose} title="Opprett ny mal" centered>
    <TextInput
      label="Malnavn"
      value={newTemplateName}
      onChange={(event) => onNewTemplateNameChange(event.currentTarget.value)}
      placeholder="Skriv inn navn for ny mal"
      required
    />
    <Group justify="flex-end" mt="md">
      <Button variant="outline" onClick={onClose}>
        Avbryt
      </Button>
      <Button variant="filled" onClick={onSaveNew} disabled={!newTemplateName}>
        Opprett
      </Button>
    </Group>
  </Modal>
);

const IssueBoardTemplateActions: FC = () => {
  const dispatch = useAppDispatch();
  const activeTemplate = useAppSelector(selectActiveTableTemplate);
  const { allColumnState, sortedCol } = useAppSelector((state) => state.issueReducerNew);
  const projectId = useAppSelector(selectCurrentProjectId);
  const issueBoardId = useAppSelector(selectIssueBoardId); // Assuming issueBoardId is in Redux
  const taskAndOrIssue = useAppSelector((s) => s.issueReducerNew.taskAndOrIssueArgs);
  const dropdownFilters = useAppSelector((s) => s.issueReducerNew.dropdownFilters);
  const [updateTableTemplate] = usePatchProjectByProjectIdTableTemplateMutation();
  const [createTableTemplate] = usePostProjectByProjectIdTableTemplateMutation();
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);
  const [isSaveNewModalOpen, setIsSaveNewModalOpen] = useState(false);
  const [templateName, setTemplateName] = useState<string>("");
  const [newTemplateName, setNewTemplateName] = useState<string>("");

  useEffect(() => {
    if (activeTemplate) {
      setTemplateName(activeTemplate.name);
    } else {
      setTemplateName("");
    }
  }, [activeTemplate]);

  const handleSaveChanges = () => {
    if (activeTemplate && projectId) {
      updateTableTemplate({
        projectId,
        body: {
          tableTemplate: {
            _id: activeTemplate._id,
            issueBoard: activeTemplate.issueBoard,
            name: templateName,
            filters: convertIssueDropdownFiltersToTableTemplateFilter(dropdownFilters),
            taskAndOrIssue: taskAndOrIssue,
            tableColumns: [], //this should be removed
            tableColConfigs: allColumnState[issueBoardId],
            sortedCol: sortedCol ?? activeTemplate.sortedCol,
          },
        },
      });
      setIsSaveChangesModalOpen(false);
    }
  };

  const handleSaveNew = () => {
    createTableTemplate({
      projectId,
      body: {
        tableTemplate: {
          name: newTemplateName,
          issueBoard: issueBoardId,
          filters: convertIssueDropdownFiltersToTableTemplateFilter(dropdownFilters),
          taskAndOrIssue: taskAndOrIssue,
          tableColumns: [], //this should be removed
          tableColConfigs: allColumnState[issueBoardId],
          sortedCol: sortedCol ?? { column: "id", direction: "DESC" },
        },
      },
    });
    setIsSaveNewModalOpen(false);
    setNewTemplateName("");
  };

  return (
    <>
      <TemplateMenu
        onSaveChanges={() => setIsSaveChangesModalOpen(true)}
        onSaveNew={() => setIsSaveNewModalOpen(true)}
        hasActiveTemplate={!!activeTemplate}
      />
      <SaveChangesModal
        isOpen={isSaveChangesModalOpen}
        onClose={() => setIsSaveChangesModalOpen(false)}
        templateName={templateName}
        onTemplateNameChange={setTemplateName}
        onSave={handleSaveChanges}
      />
      <SaveNewTemplateModal
        isOpen={isSaveNewModalOpen}
        onClose={() => setIsSaveNewModalOpen(false)}
        newTemplateName={newTemplateName}
        onNewTemplateNameChange={setNewTemplateName}
        onSaveNew={handleSaveNew}
      />
    </>
  );
};

export default IssueBoardTemplateActions;
